import handleApiError from "../../utils/handleApiError";
import { axiosInstanceApiNestingale } from "../../axios";

export interface UploadRawProductImagesRequest {
    productSkuId: string;
    imagesCount?: number;
    fileNames?: string[];
    key: string;
    fileType: string;
}

export interface UploadRawProductImageResponse {
    presignedUrls: string[];
    publicUrls: string[];
    message: string;
}

export const rawProductsApi = {
    getAllRawProducts: async (params: any = {}): Promise<any> => {
        const { vendorId, ...restParams } = params;
        try {
            const config: any = {
                params: restParams,
                extraHeaders: {
                    'Content-Type': 'application/json',
                }
            };

            if (vendorId) {
                config.skipAuthHeaders = true;
                config.extraHeaders['Supplier-Id'] = vendorId;
            }

            const response = await axiosInstanceApiNestingale.get("v1/api/rawProducts", config);
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async getRawProductById(productId: string): Promise<any[]> {
        try {
            const response = await axiosInstanceApiNestingale.get(
                `v1/api/rawProducts/${productId}`,
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const data = response.data;

            if (!Array.isArray(data) || data.length === 0) {
                throw new Error("No product data found");
            }

            const item = data[0];

            // ✅ HEAD validation moved here
            let isThreeDUrlValid = false;

            if (item?.threedImage) {
                try {
                    const head = await fetch(item.threedImage, { method: "HEAD" });
                    isThreeDUrlValid = head.ok;
                } catch {
                    isThreeDUrlValid = false;
                }
            }

            return [
                {
                    ...item,
                    isThreeDUrlValid,
                },
            ];

        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async updateRawProduct(productId: string, payload: any): Promise<string> {
        try {
            const response = await axiosInstanceApiNestingale.put(`v1/api/rawProducts/${productId}`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async patchRawProduct(productId: string, payload: any): Promise<string> {
        try {
            const response = await axiosInstanceApiNestingale.put(`v1/api/rawProducts/${productId}`, { updates: { ...payload } }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async deleteRawProduct(productId: string): Promise<string> {
        try {
            const response = await axiosInstanceApiNestingale.delete(`v1/api/rawProducts/${productId}`, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async uploadRawProductImage(payload: UploadRawProductImagesRequest): Promise<UploadRawProductImageResponse> {
        try {
            const response = await axiosInstanceApiNestingale.post('v1/api/rawProducts/uploadImages', payload, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async toggleProductVisibility(visibilityTarget: string, payload: Record<string, boolean>): Promise<any> {
        try {
            const response = await axiosInstanceApiNestingale.post(`v1/api/toggleProductVisibility?visibilityTarget=${visibilityTarget}`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
    async getProductVisibility(skus: string[]): Promise<any> {
        try {
            const response = await axiosInstanceApiNestingale.get(`v1/api/getProductVisibility?skus=${skus.join(',')}`);
            return response.data;
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    },
};